import { BRAND_META } from '../brand';
import { compact, moneyTotals, shortId } from '../format';
import type { Me, TopRow } from '../types';

const ENTITY_TITLES: Record<string, string> = {
  offer: 'Top offers',
  creator: 'Top creators',
  advertiser: 'Top advertising brands',
  merchant: 'Top merchants',
};

const DEEPLINK_ENTITY: Record<string, string> = {
  offer: 'offer',
  creator: 'creator',
  advertiser: 'advertiser',
  merchant: 'merchant',
};

/**
 * Leaderboard for one entity type over the window. Revenue leads for the
 * roles that see it; analysts get the same table ranked by activity with
 * the revenue column absent rather than blanked (§2.1.3: no financials).
 */
export function TopTable({
  rows,
  entityType,
  me,
  onDeepLink,
}: {
  rows: TopRow[];
  entityType: string;
  me: Me;
  onDeepLink?: (row: TopRow) => void;
}) {
  const filtered = rows.filter((r) => r.entity_type === entityType);
  const showMoney = me.role !== 'analyst';
  if (filtered.length === 0) return null;
  const showClicks = filtered.some((r) => r.clicks > 0);
  const canLink = onDeepLink && (me.role === 'owner' || me.role === 'operator');

  return (
    <div className="card">
      <div className="card-head">
        <span className="card-title">{ENTITY_TITLES[entityType] ?? entityType}</span>
        <span className="card-note">last 30 days</span>
      </div>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>{entityType}</th>
              {showClicks && <th className="num">Clicks</th>}
              <th className="num">Txns</th>
              {showMoney && <th className="num">Revenue</th>}
              {canLink && <th className="link-col" aria-label="open" />}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={`${r.brand}-${r.entity_id}`}>
                <td title={r.entity_id}>
                  <span className="entity-swatch" style={{ background: BRAND_META[r.brand].series }} aria-hidden />
                  {/* The learned name, else the id shortened rather than a
                      wall of UUID; the full id stays in the tooltip and
                      the deep link. Names flow in via metrics.entity_names
                      the moment the trackers send them. */}
                  {r.entity_name ?? <span className="entity-id">{shortId(r.entity_id)}</span>}
                </td>
                {showClicks && <td className="num">{compact(r.clicks)}</td>}
                <td className="num">{compact(r.conversions)}</td>
                {showMoney && <td className="num">{moneyTotals(r.revenue)}</td>}
                {canLink && (
                  <td className="link-col">
                    <button
                      className="deeplink"
                      onClick={() => onDeepLink(r)}
                      title={`Open in the ${BRAND_META[r.brand].label} admin`}
                    >
                      Open in admin →
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function deeplinkEntityFor(entityType: string): string | null {
  return DEEPLINK_ENTITY[entityType] ?? null;
}
